import { useEffect, useRef, useState } from "react";
import { resolveDisplayProfile, type DisplayViewport, type ResolvedDisplayProfile } from "./profileMatcher";
import { createDisplayProfileVariables, type DisplayCssVariables } from "./profileVariables";

export interface DisplayProfileState {
  resolved: ResolvedDisplayProfile;
  variables: DisplayCssVariables;
}

function readViewport(): DisplayViewport {
  const visualViewport = window.visualViewport;
  return {
    width: Math.round(visualViewport?.width || window.innerWidth || 0),
    height: Math.round(visualViewport?.height || window.innerHeight || 0),
    devicePixelRatio: window.devicePixelRatio || 1,
    visualScale: visualViewport?.scale || 1,
    screenWidth: window.screen?.width || 0,
    screenHeight: window.screen?.height || 0
  };
}

function createState(): DisplayProfileState {
  const resolved = resolveDisplayProfile(readViewport());
  return { resolved, variables: createDisplayProfileVariables(resolved) };
}

export function useDisplayProfile() {
  const ref = useRef<HTMLElement | null>(null);
  const [state, setState] = useState<DisplayProfileState>(createState);

  useEffect(() => {
    function updateProfile() {
      const next = createState();
      if (next.resolved.stage.width === 0 || next.resolved.stage.height === 0) {
        return;
      }
      setState((current) => current.resolved.profile.id === next.resolved.profile.id
        && current.resolved.stage.width === next.resolved.stage.width
        && current.resolved.stage.height === next.resolved.stage.height
        && current.resolved.distance === next.resolved.distance
        ? current
        : next);
    }

    updateProfile();
    window.addEventListener("resize", updateProfile);
    window.addEventListener("orientationchange", updateProfile);
    window.addEventListener("fullscreenchange", updateProfile);
    window.visualViewport?.addEventListener("resize", updateProfile);
    window.visualViewport?.addEventListener("scroll", updateProfile);

    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateProfile);
    if (observer && ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      window.removeEventListener("resize", updateProfile);
      window.removeEventListener("orientationchange", updateProfile);
      window.removeEventListener("fullscreenchange", updateProfile);
      window.visualViewport?.removeEventListener("resize", updateProfile);
      window.visualViewport?.removeEventListener("scroll", updateProfile);
      observer?.disconnect();
    };
  }, []);

  return { ref, ...state };
}
